import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { photoUrl, carTitle, fmtPrice, fmtNum, trField } from "../../lib/api";
import { LogoMark } from "./Logo";

export const StatusBadge = ({ status }) => {
  const { t } = useI18n();
  if (!status || status === "available") return null;
  const cls = status === "sold" ? "bg-red-600 text-white" : "bg-amber-400 text-ink";
  return <span className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${cls}`} data-testid="car-status-badge">{t.status[status]}</span>;
};

const onSpot = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

export const CarCard = ({ car, index = 0 }) => {
  const { t, locale, lang } = useI18n();
  const version = trField(car, "version", lang);
  const cover = photoUrl(car.photos?.[0]);
  const meta = [
    car.year,
    car.km != null ? `${fmtNum(car.km, locale)} km` : null,
    car.cv ? `${car.cv} CV` : null,
    car.fuel ? t.fuel[car.fuel] || car.fuel : null,
  ].filter(Boolean);

  return (
    <Link to={`/auto/${car.id}`} className="group block" data-testid="car-card" onMouseMove={onSpot}>
      <div className="spotlight relative aspect-[4/3] overflow-hidden rounded-[22px] bg-[#EDEDEA]">
        {cover ? (
          <img src={cover} alt={carTitle(car)} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.03]" />
        ) : (
          <div className="grid h-full w-full place-items-center"><LogoMark className="h-16 w-16 opacity-80" /></div>
        )}
        <div className="absolute left-4 top-4 flex gap-2">
          <span className="rounded-full bg-white/90 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink backdrop-blur">N° {String(index + 1).padStart(2, "0")}</span>
          <StatusBadge status={car.status} />
        </div>
        <span className="absolute bottom-4 right-4 grid h-11 w-11 translate-y-3 place-items-center rounded-full bg-pearl text-ink opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
          <ArrowUpRight className="h-5 w-5" />
        </span>
      </div>
      <div className="mt-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-champagne-dark">{car.make || "—"}</p>
          <h3 className="mt-1 truncate font-display text-xl font-bold uppercase tracking-tight text-ink sm:text-2xl" data-testid="car-card-title">{car.model || carTitle(car)}</h3>
          {version && <p className="truncate text-sm text-slate-600">{version}</p>}
        </div>
        <p className="shrink-0 pt-5 font-display text-lg font-bold text-ink" data-testid="car-card-price">
          {car.price ? fmtPrice(car.price, locale) : <span className="text-sm font-medium text-slate-600">{t.inv.priceOnRequest}</span>}
        </p>
      </div>
      {meta.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 border-t border-ink/10 pt-3 font-mono text-xs uppercase tracking-wider text-slate-600">
          {meta.map((m) => <span key={m}>{m}</span>)}
        </div>
      )}
    </Link>
  );
};
